import { useEffect, useRef, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  PaperPlaneRight,
  ChatCircle,
  MapPin,
} from "@phosphor-icons/react";

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function clock(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function Inbox({ conversations, refreshConversations }) {
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadThread = useCallback(async (customerId) => {
    try {
      const { data } = await api.get(`/conversations/${customerId}/messages`);
      setMessages(data.messages);
      setCustomer(data.customer);
    } catch (e) {
      /* silent */
    }
  }, []);

  useEffect(() => {
    if (!selected) return;
    loadThread(selected);
    const id = setInterval(() => loadThread(selected), 4000);
    return () => clearInterval(id);
  }, [selected, loadThread]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  async function send() {
    if (!body.trim() || !selected) return;
    setSending(true);
    try {
      await api.post("/messages/send", { customer_id: selected, body: body.trim() });
      setBody("");
      await loadThread(selected);
      refreshConversations();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex-1 flex min-h-0">
      {/* Conversation list */}
      <div className="w-80 xl:w-96 shrink-0 border-r border-border flex flex-col min-h-0">
        <div className="h-16 flex items-center px-5 border-b border-border">
          <h1 className="text-xl font-semibold tracking-tight">Inbox</h1>
          <span className="ml-auto text-xs text-muted-foreground font-mono">
            {conversations.length} threads
          </span>
        </div>
        <ScrollArea className="flex-1">
          {conversations.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No conversations yet. Send an SMS or sync customers to get started.
            </div>
          ) : (
            conversations.map((c, i) => {
              const active = selected === c.customer.id;
              return (
                <button
                  key={c.customer.id}
                  data-testid={`conversation-${c.customer.id}`}
                  onClick={() => setSelected(c.customer.id)}
                  style={{ animationDelay: `${Math.min(i * 30, 300)}ms` }}
                  className={`animate-fade-up w-full text-left p-3 border-b border-border transition-colors duration-100 ${
                    active ? "bg-accent" : "hover:bg-accent/50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {c.unread > 0 && (
                      <span className="h-2 w-2 rounded-full bg-primary shrink-0" data-testid="unread-dot" />
                    )}
                    <span className={`text-sm truncate flex-1 ${c.unread > 0 ? "font-semibold" : "font-medium"}`}>
                      {c.customer.name}
                    </span>
                    <span className="text-[10px] text-muted-foreground font-mono shrink-0">
                      {timeAgo(c.last_message.created_at)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 pl-0">
                    <span className="text-xs text-muted-foreground truncate flex-1">
                      {c.last_message.direction === "outbound" && "You: "}
                      {c.last_message.body}
                    </span>
                  </div>
                  {c.customer.tags && c.customer.tags.length > 0 && (
                    <div className="text-[10px] text-muted-foreground/70 font-mono mt-1 flex items-center gap-1 flex-wrap">
                      <MapPin size={10} /> {c.customer.tags.join(", ")}
                    </div>
                  )}
                </button>
              );
            })
          )}
        </ScrollArea>
      </div>

      {/* Thread */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {!selected ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3">
            <ChatCircle size={48} weight="thin" />
            <p className="text-sm">Select a conversation to view messages</p>
          </div>
        ) : (
          <>
            <div className="h-16 flex items-center px-6 border-b border-border">
              <div className="leading-tight">
                <div className="font-semibold tracking-tight" data-testid="thread-customer-name">
                  {customer?.name}
                </div>
                <div className="text-xs text-muted-foreground font-mono flex items-center gap-2">
                  <span>{customer?.phone}</span>
                  {customer?.tags && customer.tags.length > 0 && (
                    <>
                      <span className="text-border">·</span>
                      <span className="flex items-center gap-1">
                        <MapPin size={11} /> {customer.tags.join(", ")}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-3">
              {messages.map((m) => {
                const out = m.direction === "outbound";
                return (
                  <div
                    key={m.id}
                    className={`flex ${out ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`animate-bubble-in max-w-[70%] px-3.5 py-2 rounded-sm text-sm ${
                        out
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary border border-border text-foreground"
                      }`}
                    >
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                      <div
                        className={`text-[10px] font-mono mt-1 ${
                          out ? "text-primary-foreground/70" : "text-muted-foreground"
                        }`}
                      >
                        {clock(m.created_at)}
                        {out && ` · ${m.status}`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-border p-4">
              <div className="flex items-end gap-3">
                <Textarea
                  data-testid="message-input"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Type a message…  (Enter to send)"
                  rows={1}
                  className="rounded-sm resize-none min-h-[42px] max-h-32"
                />
                <Button
                  data-testid="send-message-button"
                  onClick={send}
                  disabled={sending || !body.trim()}
                  className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground h-[42px] gap-2"
                >
                  <PaperPlaneRight size={16} weight="fill" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
